import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Form as FinalForm } from 'react-final-form';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { postFinanceLead } from '../../util/api';
import { CREDIT_TIERS } from '../../containers/PaymentCalculator/calculationEngine';
import {
  Modal,
  Form,
  FieldTextInput,
  FieldSelect,
  FieldRadioButton,
  FieldCheckbox,
  PrimaryButton,
  NamedLink,
} from '..';

import css from './FinanceLeadForm.module.css';

const MODAL_ID = 'FinanceLeadForm';

const newIdempotencyKey = () =>
  typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function'
    ? window.crypto.randomUUID()
    : `k${Date.now().toString(36)}${Math.random()
        .toString(36)
        .slice(2)}${Math.random()
        .toString(36)
        .slice(2)}`;

const EMPLOYMENT_STATUSES = [
  'employedFullTime',
  'employedPartTime',
  'selfEmployed',
  'retired',
  'student',
  'notEmployed',
  'other',
];

// Income is required for a financing review, so there is deliberately no
// "prefer not to say" option here. A buyer who does not want to give it can
// switch the form to a general inquiry instead -- see LEAD_TYPES below.
const INCOME_RANGES = [
  'under2000',
  'r2000to3499',
  'r3500to4999',
  'r5000to7499',
  'r7500to9999',
  'over10000',
];

// What this submission actually is. A general inquiry is never presented,
// labelled, or submitted as a finance pre-approval: without income there is
// nothing a lender can review, and calling it a pre-approval would misdescribe
// it to the buyer and to the finance team.
const LEAD_TYPES = {
  preApproval: 'finance-preapproval',
  generalInquiry: 'general-inquiry',
};

/**
 * Structured finance pre-approval lead form, opened from PurchaseOptions'
 * FinanceTab "Get pre-approved" CTA. This collects financing data directly
 * -- it does not run through Hannah/chat. Hannah remains available
 * separately for guidance and explanation, not for capturing this data.
 *
 * Never approves financing, promises a rate, or promises approval in this
 * UI, and never collects payment here -- it only submits a lead for a human
 * finance team to review.
 *
 * Income is required for the pre-approval path: a lender cannot review an
 * application without it. Rather than accepting a blank, the form offers to
 * become a general inquiry instead -- which is titled, submitted, confirmed
 * and recorded as an inquiry, never as a pre-approval.
 */
export default function FinanceLeadForm({
  isOpen,
  onClose,
  listingId,
  sellerType,
  purchaseOption, // { tab: 'finance', termMonths, rate, frequency }
  estimate, // { tax, deliveryEstimate, registrationEstimate, total }
}) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [leadType, setLeadType] = useState(LEAD_TYPES.preApproval);
  // One key per application, reused on every retry of it, so the server
  // stores one application however many times the request is sent.
  const idempotencyKeyRef = useRef(null);

  const isPreApproval = leadType === LEAD_TYPES.preApproval;
  const isChecked = value => Array.isArray(value) && value.length > 0;

  const handleSubmit = async values => {
    setSubmitError('');
    const hasTradeIn = values.hasTradeIn === 'yes';
    const num = value => (value === '' || value == null ? null : Number(value));
    if (!idempotencyKeyRef.current) idempotencyKeyRef.current = newIdempotencyKey();
    try {
      const data = await postFinanceLead({
        idempotencyKey: idempotencyKeyRef.current,
        leadType,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        email: values.email,
        postalCode: values.postalCode,
        listingId,
        sellerType,
        paymentTarget: purchaseOption?.payment ?? null,
        paymentFrequency: purchaseOption?.frequency ?? null,
        downPayment: values.downPayment ? Number(values.downPayment) : 0,
        hasTradeIn,
        tradeValueEstimate: hasTradeIn ? num(values.tradeValueEstimate) : null,
        tradePayout: hasTradeIn ? num(values.tradePayout) : null,
        tradeLender: hasTradeIn ? values.tradeLender || '' : '',
        // Left null rather than defaulted: "we did not ask/they did not say"
        // is not the same answer as "no".
        tradeInGoodStanding:
          hasTradeIn && values.tradeInGoodStanding ? values.tradeInGoodStanding === 'yes' : null,
        creditRange: values.creditRange,
        employmentStatus: values.employmentStatus,
        // A general inquiry carries no income at all -- not a blank, not a
        // placeholder value that could later read as a declared figure.
        incomeRange: isPreApproval ? values.incomeRange : null,
        consentToContact: isChecked(values.consentToContact),
        consentToSubmitFinanceInfo: isPreApproval
          ? isChecked(values.consentToSubmitFinanceInfo)
          : false,
        notes: values.notes || '',
        purchaseOption,
        estimate,
      });
      // Success only with a stored application's ID — never on a bare flag.
      if (data && data.success && data.applicationId) {
        setSubmitted(true);
      } else {
        setSubmitError(data.error || intl.formatMessage({ id: 'FinanceLeadForm.genericError' }));
      }
    } catch {
      setSubmitError(intl.formatMessage({ id: 'FinanceLeadForm.networkError' }));
    }
  };

  const emailValidators = validators.composeValidators(
    validators.required(intl.formatMessage({ id: 'FinanceLeadForm.emailRequired' })),
    validators.emailFormatValid(intl.formatMessage({ id: 'FinanceLeadForm.emailInvalid' }))
  );
  const requiredField = messageId => validators.required(intl.formatMessage({ id: messageId }));

  return (
    <Modal
      id={MODAL_ID}
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={(id, disable) => dispatch(manageDisableScrolling(id, disable))}
      usePortal
    >
      {submitted ? (
        <div className={css.confirmation}>
          <p className={css.confirmationTitle}>
            <FormattedMessage
              id={
                isPreApproval
                  ? 'FinanceLeadForm.confirmationTitle'
                  : 'FinanceLeadForm.inquiryConfirmationTitle'
              }
            />
          </p>
          <p className={css.confirmationBody}>
            <FormattedMessage
              id={
                isPreApproval
                  ? 'FinanceLeadForm.confirmationBody'
                  : 'FinanceLeadForm.inquiryConfirmationBody'
              }
            />
          </p>
          <p className={css.compliance}>
            <FormattedMessage id="FinanceLeadForm.complianceCopy" />
          </p>
          <NamedLink className={css.hannahLink} name="LandingPage">
            <FormattedMessage id="FinanceLeadForm.chatWithHannah" />
          </NamedLink>
        </div>
      ) : (
        <>
          <p className={css.title}>
            <FormattedMessage
              id={isPreApproval ? 'FinanceLeadForm.title' : 'FinanceLeadForm.inquiryTitle'}
            />
          </p>
          <p className={css.body}>
            <FormattedMessage
              id={isPreApproval ? 'FinanceLeadForm.body' : 'FinanceLeadForm.inquiryBody'}
            />
          </p>
          <FinalForm
            onSubmit={handleSubmit}
            render={formRenderProps => {
              const { handleSubmit: onFormSubmit, submitting, invalid, values } = formRenderProps;
              const hasTradeIn = values.hasTradeIn === 'yes';
              // A general inquiry submits no financing information, so the
              // consent to send financing data to a lender does not apply.
              const consentGiven =
                isChecked(values.consentToContact) &&
                (!isPreApproval || isChecked(values.consentToSubmitFinanceInfo));
              const submitDisabled = invalid || submitting || !consentGiven;

              return (
                <Form onSubmit={onFormSubmit}>
                  <div className={css.row}>
                    <FieldTextInput
                      className={css.field}
                      type="text"
                      id="FinanceLeadForm.firstName"
                      name="firstName"
                      label={intl.formatMessage({ id: 'FinanceLeadForm.firstNameLabel' })}
                      validate={requiredField('FinanceLeadForm.firstNameRequired')}
                    />
                    <FieldTextInput
                      className={css.field}
                      type="text"
                      id="FinanceLeadForm.lastName"
                      name="lastName"
                      label={intl.formatMessage({ id: 'FinanceLeadForm.lastNameLabel' })}
                      validate={requiredField('FinanceLeadForm.lastNameRequired')}
                    />
                  </div>
                  <div className={css.row}>
                    <FieldTextInput
                      className={css.field}
                      type="tel"
                      id="FinanceLeadForm.phone"
                      name="phone"
                      label={intl.formatMessage({ id: 'FinanceLeadForm.phoneLabel' })}
                      validate={requiredField('FinanceLeadForm.phoneRequired')}
                    />
                    <FieldTextInput
                      className={css.field}
                      type="email"
                      id="FinanceLeadForm.email"
                      name="email"
                      label={intl.formatMessage({ id: 'FinanceLeadForm.emailLabel' })}
                      validate={emailValidators}
                    />
                  </div>
                  <FieldTextInput
                    className={css.field}
                    type="text"
                    id="FinanceLeadForm.postalCode"
                    name="postalCode"
                    label={intl.formatMessage({ id: 'FinanceLeadForm.postalCodeLabel' })}
                    validate={requiredField('FinanceLeadForm.postalCodeRequired')}
                  />

                  <div className={css.row}>
                    <FieldSelect
                      className={css.field}
                      id="FinanceLeadForm.creditRange"
                      name="creditRange"
                      label={intl.formatMessage({ id: 'FinanceLeadForm.creditRangeLabel' })}
                    >
                      <option disabled value="">
                        {intl.formatMessage({ id: 'FinanceLeadForm.selectPlaceholder' })}
                      </option>
                      <option value="notSure">
                        {intl.formatMessage({ id: 'FinanceLeadForm.creditRangeNotSure' })}
                      </option>
                      {CREDIT_TIERS.map(tier => (
                        <option key={tier.value} value={tier.value}>
                          {tier.label}
                        </option>
                      ))}
                    </FieldSelect>
                    <FieldSelect
                      className={css.field}
                      id="FinanceLeadForm.employmentStatus"
                      name="employmentStatus"
                      label={intl.formatMessage({ id: 'FinanceLeadForm.employmentStatusLabel' })}
                    >
                      <option disabled value="">
                        {intl.formatMessage({ id: 'FinanceLeadForm.selectPlaceholder' })}
                      </option>
                      {EMPLOYMENT_STATUSES.map(key => (
                        <option key={key} value={key}>
                          {intl.formatMessage({ id: `FinanceLeadForm.employmentStatus.${key}` })}
                        </option>
                      ))}
                    </FieldSelect>
                  </div>

                  {isPreApproval ? (
                    <div className={css.incomeBlock}>
                      <FieldSelect
                        className={css.field}
                        id="FinanceLeadForm.incomeRange"
                        name="incomeRange"
                        label={intl.formatMessage({ id: 'FinanceLeadForm.incomeRangeLabel' })}
                        validate={requiredField('FinanceLeadForm.incomeRequired')}
                      >
                        <option disabled value="">
                          {intl.formatMessage({ id: 'FinanceLeadForm.selectPlaceholder' })}
                        </option>
                        {INCOME_RANGES.map(key => (
                          <option key={key} value={key}>
                            {intl.formatMessage({ id: `FinanceLeadForm.incomeRange.${key}` })}
                          </option>
                        ))}
                      </FieldSelect>
                      <p className={css.incomeHelp}>
                        <FormattedMessage id="FinanceLeadForm.incomeHelp" />
                      </p>
                      {/* The way out for someone who would rather not give
                          income. It switches what this submission is -- it
                          does not submit an income-less pre-approval. */}
                      <button
                        type="button"
                        className={css.leadTypeToggle}
                        onClick={() => setLeadType(LEAD_TYPES.generalInquiry)}
                      >
                        <FormattedMessage id="FinanceLeadForm.continueAsInquiry" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className={css.leadTypeToggle}
                      onClick={() => setLeadType(LEAD_TYPES.preApproval)}
                    >
                      <FormattedMessage id="FinanceLeadForm.backToPreApproval" />
                    </button>
                  )}

                  <FieldTextInput
                    className={css.field}
                    type="number"
                    id="FinanceLeadForm.downPayment"
                    name="downPayment"
                    label={intl.formatMessage({ id: 'FinanceLeadForm.downPaymentLabel' })}
                  />

                  <fieldset className={css.tradeInFieldset}>
                    <legend className={css.tradeInLegend}>
                      <FormattedMessage id="FinanceLeadForm.hasTradeInLabel" />
                    </legend>
                    <div className={css.radioRow}>
                      <FieldRadioButton
                        id="FinanceLeadForm.hasTradeIn.yes"
                        name="hasTradeIn"
                        value="yes"
                        label={intl.formatMessage({ id: 'FinanceLeadForm.tradeInYes' })}
                      />
                      <FieldRadioButton
                        id="FinanceLeadForm.hasTradeIn.no"
                        name="hasTradeIn"
                        value="no"
                        label={intl.formatMessage({ id: 'FinanceLeadForm.tradeInNo' })}
                      />
                    </div>
                  </fieldset>

                  {hasTradeIn ? (
                    <>
                      <div className={css.row}>
                        <FieldTextInput
                          className={css.field}
                          type="number"
                          id="FinanceLeadForm.tradeValueEstimate"
                          name="tradeValueEstimate"
                          label={intl.formatMessage({
                            id: 'FinanceLeadForm.tradeValueEstimateLabel',
                          })}
                        />
                        {/* The payout, not "amount owing": the balance shown on
                            a statement and the figure needed to clear the loan
                            are not the same number, and it is the payout that
                            decides whether there is negative equity. */}
                        <FieldTextInput
                          className={css.field}
                          type="number"
                          id="FinanceLeadForm.tradePayout"
                          name="tradePayout"
                          label={intl.formatMessage({ id: 'FinanceLeadForm.tradePayoutLabel' })}
                        />
                      </div>
                      <FieldTextInput
                        className={css.field}
                        type="text"
                        id="FinanceLeadForm.tradeLender"
                        name="tradeLender"
                        label={intl.formatMessage({ id: 'FinanceLeadForm.tradeLenderLabel' })}
                      />
                      <fieldset className={css.tradeInFieldset}>
                        <legend className={css.tradeInLegend}>
                          <FormattedMessage id="FinanceLeadForm.tradeGoodStandingLabel" />
                        </legend>
                        <div className={css.radioRow}>
                          <FieldRadioButton
                            id="FinanceLeadForm.tradeInGoodStanding.yes"
                            name="tradeInGoodStanding"
                            value="yes"
                            label={intl.formatMessage({ id: 'FinanceLeadForm.goodStandingYes' })}
                          />
                          <FieldRadioButton
                            id="FinanceLeadForm.tradeInGoodStanding.no"
                            name="tradeInGoodStanding"
                            value="no"
                            label={intl.formatMessage({ id: 'FinanceLeadForm.goodStandingNo' })}
                          />
                        </div>
                      </fieldset>
                    </>
                  ) : null}

                  <FieldTextInput
                    className={css.field}
                    type="textarea"
                    id="FinanceLeadForm.notes"
                    name="notes"
                    label={intl.formatMessage({ id: 'FinanceLeadForm.notesLabel' })}
                  />

                  <div className={css.consentBlock}>
                    <FieldCheckbox
                      id="FinanceLeadForm.consentToContact"
                      name="consentToContact"
                      value="given"
                      label={intl.formatMessage({ id: 'FinanceLeadForm.consentToContact' })}
                    />
                    {isPreApproval ? (
                      <FieldCheckbox
                        id="FinanceLeadForm.consentToSubmitFinanceInfo"
                        name="consentToSubmitFinanceInfo"
                        value="given"
                        label={intl.formatMessage({
                          id: 'FinanceLeadForm.consentToSubmitFinanceInfo',
                        })}
                      />
                    ) : null}
                  </div>

                  <p className={css.compliance}>
                    <FormattedMessage id="FinanceLeadForm.complianceCopy" />
                  </p>

                  {submitError ? <div className={css.error}>{submitError}</div> : null}

                  <PrimaryButton
                    className={css.submitButton}
                    type="submit"
                    inProgress={submitting}
                    disabled={submitDisabled}
                  >
                    <FormattedMessage
                      id={
                        isPreApproval
                          ? 'FinanceLeadForm.submitButton'
                          : 'FinanceLeadForm.inquirySubmitButton'
                      }
                    />
                  </PrimaryButton>
                </Form>
              );
            }}
          />
        </>
      )}
    </Modal>
  );
}
